import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { ReportStatus, type ReportFilters, type RotaryKiln } from '../../types/reports';
import { previousYear } from '../../utils/reportPeriod';
import { formatTonnage } from '../../utils/formatting';
// Namespace-Import (Vorbild: `totalsModule` in `components/reports/formularD/DerivedTotal.tsx`)
// -- haelt den Bezeichner der Abbildungsfunktion aus 24-03 auf genau EINE Fundstelle im Modul
// begrenzt (den Aufruf selbst), statt ihn zusaetzlich in einer Import-Zeile zu wiederholen.
import * as previousValuesSource from './previousValuesSource';
import type { PreviousValuesLoadState } from './previousValuesHint';
import type { PreviousValues, PreviousValuesStore, ValuesVersionStore } from './formStores';
import { calculateReport } from '../../services/reportsApi';
import { FORMULAR_D_CALC_INPUT_KEYS, FORMULAR_D_CALC_OUTPUT_KEYS } from './formularD/catalog';

/**
 * Nicht blockierende Ladekette Vorjahresmeldung -> `PreviousValuesStore` (D-01, D-02, D-07,
 * D-09, D-10). Der Hook holt die Vorjahresmeldung EINES Werks/EINER Periode ueber den
 * bestehenden Mehrwert-Statusfilter (24-01), baut daraus die Vorwerte ueber die
 * Abbildungsschicht aus 24-03 und schreibt sie in den geteilten Store (24-02). Kein
 * Backend-Change (D-18) -- der Abruf laeuft ausschliesslich ueber die bestehenden
 * Collection-Filter der fuenf Ressourcen.
 */

/**
 * Die zwei Status, gegen die eine Vorjahresmeldung als belastbare Vergleichsbasis zaehlt.
 *
 * `revision_needed` gehoert AUSDRUECKLICH NICHT dazu: der Status bedeutet "eingereicht und
 * fachlich beanstandet". Gegen beanstandete Zahlen zu pruefen schriebe den Fehler fort --
 * dieselbe Begruendung, aus der Entwuerfe (`draft`, `in_progress`) ausgeschlossen sind (D-02).
 */
export const PREVIOUS_VALUE_STATUSES: readonly ReportStatus[] = [
  ReportStatus.SUBMITTED,
  ReportStatus.APPROVED,
];

/** Kennung des Zwischenspeichers -- identifiziert einen Ladelauf eindeutig ueber Formulartyp, Werk und Vorjahresperiode. */
export function previousValuesCacheKey(
  kind: string,
  plantId: number | undefined,
  previousPeriod: string | null,
): string {
  return `${kind}:${plantId ?? ''}:${previousPeriod ?? ''}`;
}

/**
 * Loest eine Ergebnisliste des Vorjahres-`GET` auf den ersten Treffer auf.
 *
 * Es gibt keinen Unique-Constraint auf plant+reportingPeriod+Typ (24-RESEARCH.md Fallstrick 5)
 * -- eine leere Liste bedeutet "keine Vorjahresmeldung" (`missing`, D-06), eine Liste mit mehr
 * als einem Treffer darf nicht zum Absturz fuehren, sondern nimmt defensiv den ERSTEN.
 */
export function resolvePreviousReport<T>(list: T[]): {
  report: T | null;
  state: 'loaded' | 'missing';
} {
  if (list.length === 0) {
    return { report: null, state: 'missing' };
  }
  return { report: list[0], state: 'loaded' };
}

/**
 * Baut den Payload fuer den zweiten, verketteten Rechenaufruf der Formular-D-Vorjahres-Summen
 * (D-11): genau die 277 rechenrelevanten Schluessel aus `FORMULAR_D_CALC_INPUT_KEYS`, in
 * derselben Form, die `useReportCalc.ts:80-82` fuer die LAUFENDE Meldung an
 * `POST /api/reports/calculate` schickt (`values[key] ?? null`, nicht `?? ''` wie beim
 * Debounce-Schluessel `buildCalcKey`). Ein im hydrierten Vorjahresdatensatz fehlender
 * Schluessel erscheint trotzdem im Payload -- mit demselben Ersatzwert -- damit die Payload-Form
 * stabil bleibt.
 */
export function buildFormularDCalcPayload(hydrated: Record<string, unknown>): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const key of FORMULAR_D_CALC_INPUT_KEYS) {
    payload[key] = hydrated[key] ?? null;
  }
  return payload;
}

/**
 * Bildet die Antwort des Rechenaufrufs auf die 41 Vorjahres-Summenschluessel aus
 * `FORMULAR_D_CALC_OUTPUT_KEYS` ab, formatiert wie jeder andere Vorwert ueber `formatTonnage`
 * (D-14). Ein in der Antwort fehlender Schluessel ergibt '–' (D-08) -- `formatTonnage`
 * uebernimmt das bereits fuer `undefined`. `mapFormularDCalcResult({})` -- die leere Antwort des
 * Fehlerfalls (D-21) -- liefert dadurch fuer alle 41 Schluessel '–', ohne eine zweite
 * Ersatzregel zu brauchen.
 */
export function mapFormularDCalcResult(result: Record<string, unknown>): Record<string, string> {
  const mapped: Record<string, string> = {};
  for (const key of FORMULAR_D_CALC_OUTPUT_KEYS) {
    mapped[key] = formatTonnage(result[key] as string | number | null | undefined);
  }
  return mapped;
}

interface PreviousValuesLoaderApi<T> {
  getAll: (filters?: ReportFilters) => Promise<T[]>;
}

// Bewusst ohne `extends Record<string, unknown>`: die fuenf konkreten Report-Typen
// (FormularAB, FormularC, FormularD, EnvironmentalData, AccidentData) haben kein
// Index-Signatur und sind einer solchen Einschraenkung strukturell nicht zuweisbar -- exakt
// derselbe TS-Befund, den `AnyReportTypeDescriptor` in `ReportEditPage.tsx` bereits mit einem
// zentralisierten Cast beantwortet (siehe Kommentar dort). `T` wird hier nur durchgereicht,
// nie selbst als Record indiziert; die einzige Stelle, die ein Objekt mit Index-Signatur
// braucht (`buildPreviousValues`), bekommt einen expliziten Cast.
export interface UsePreviousValuesLoaderOptions<T> {
  /** Schalterzustand (D-09) -- bei `false` erfolgt kein Netzaufruf. */
  enabled: boolean;
  kind: string;
  api: PreviousValuesLoaderApi<T>;
  hydrate: (data: T) => Record<string, unknown>;
  plantId: number | undefined;
  reportingPeriod: string | undefined;
  store: PreviousValuesStore;
  /**
   * Lesezugriff auf den LEBENDEN Formularzustand (`form.getValues`) -- nur fuer
   * `kind === 'environmental'` ausgewertet, liefert die Ofenliste fuer die Positionszuordnung
   * der Vorjahreswerte (`kiln.plantKiln.id`). Fuer die vier anderen Formulartypen ohne Wirkung.
   *
   * Bewusst ein GETTER und nicht die geladene Meldung: der Vorwert muss zu dem passen, was am
   * Bildschirm steht, und das ist der Bearbeitungsstand -- nicht der Stand vom Server. Eine
   * gerade angelegte Messung existiert ausschliesslich hier (Fund der 28-09-Abnahme: mit
   * `currentReport` als Quelle blieb `valuesVersion`/der Formschluessel unveraendert, und die
   * neue Messung bekam ihre Spannweitenzeile erst nach einem Neuladen der Seite).
   *
   * Arbeitsteilung: Metadaten (`plantId`, `reportingPeriod`) kommen weiter vom Server -- sie
   * stehen nicht im Formular. Feldwerte kommen ausschliesslich von hier.
   */
  currentValues?: () => Record<string, unknown>;
  /**
   * Der Zaehlerspeicher aus `useReportForm`, der bei jeder Werteaenderung hochzaehlt --
   * inklusive `insertListItem`/`removeListItem` und dem `form.setValues` des Ladewegs. Er ist
   * das Aenderungssignal fuer `currentValues`; ein Getter allein kann nichts ausloesen.
   *
   * Bewusst der STORE und nicht sein Zahlenwert: `formStores.ts:3-8` haelt fest, dass dieser
   * Zaehler nicht zu Parent-State werden darf. Ein `useValuesVersion(...)` an dieser Stelle
   * saesse in `ReportEditPage` und wuerde die gesamte Meldungsseite bei jedem Tastendruck neu
   * rendern -- genau der Effekt, den das uncontrolled-Muster aus Phase 18 vermeidet. Statt-
   * dessen abonniert der Hook unten den Store mit dem FORMSCHLUESSEL als Snapshot: der bleibt
   * beim Tippen gleich, `useSyncExternalStore` rendert also nur beim Anlegen/Entfernen.
   */
  valuesVersionStore?: ValuesVersionStore;
}

export interface UsePreviousValuesLoaderResult {
  state: PreviousValuesLoadState;
  previousPeriod: string | null;
  previousReport: Record<string, unknown> | null;
  /**
   * Der Teilfehlschlag aus D-21: bei Formular D gelingt der `GET`, nur der zweite Aufruf fuer
   * die 41 Summen (`POST /api/reports/calculate`) scheitert. `true` ausschliesslich in genau
   * diesem Fall -- fuer die vier anderen Formulartypen (kein zweiter Aufruf) und bei
   * gelungenem zweiten Aufruf bleibt es `false`. `ReportEditPage` reicht den Wert unveraendert
   * an `previousValuesHintText` durch (24-05).
   */
  totalsFailed: boolean;
}

interface CachedRun {
  key: string;
  state: 'loaded' | 'missing';
  values: PreviousValues;
  previousReport: Record<string, unknown> | null;
  totalsFailed: boolean;
}

export function usePreviousValuesLoader<T>(
  options: UsePreviousValuesLoaderOptions<T>,
): UsePreviousValuesLoaderResult {
  const {
    enabled,
    kind,
    api,
    hydrate,
    plantId,
    reportingPeriod,
    store,
    currentValues,
    valuesVersionStore,
  } = options;

  // D-01: Vorjahr, gleiche Periode -- reine Frontend-Arithmetik, keine Serverrunde. Ohne
  // `reportingPeriod` (die laufende Meldung ist noch nicht geladen) ist sie `null`.
  const previousPeriod = reportingPeriod ? previousYear(reportingPeriod) : null;

  // Phase 28 (D-15/D-16): Ofenliste der AKTUELLEN Meldung, nur fuer die Umwelterhebung
  // ausgewertet -- die vier anderen Formulartypen kennen keine `rotaryKilns`.
  //
  // Quelle ist der LEBENDE Formularzustand, nicht die geladene Meldung: `hydrateRotaryKilns`
  // (hydrate.ts:109-127) reicht den Ofen per Spread durch, `plantKiln` und `id` ueberleben die
  // Hydrierung also unveraendert -- die Identitaet fuer `remapKilnPositions` ist hier genauso
  // vorhanden wie im API-Objekt, aber zusaetzlich sind ungespeicherte Messungen enthalten.
  const readCurrentKilns = useCallback(
    (): RotaryKiln[] | undefined =>
      kind === 'environmental'
        ? (currentValues?.() as { rotaryKilns?: RotaryKiln[] } | undefined)?.rotaryKilns
        : undefined,
    [kind, currentValues],
  );

  // Ausloeser fuer die Neubelegung der Ofen-Vorwerte (T-28-15): aendert sich nur beim
  // Anlegen/Entfernen eines Ofens oder einer Messung, NICHT beim Tippen in ein Feld -- eine
  // gerade neu angelegte Messung bekommt damit ihre Spannweitenzeile ohne Neuladen der Seite.
  //
  // "Neubelegung", nicht "Neuberechnung": die Vorjahreswerte selbst aendern sich nie. Die
  // Spannweite ist eine Aussage je Merkmal und Ofen und wird unter JEDEN Messungsindex des
  // aktuellen Ofens gelegt (previousValuesSource.ts:412-416). Kommt eine Messung dazu, entsteht
  // ein neuer Feldpfad -- derselbe Text muss auf die gewachsene Schluesselmenge abgebildet
  // werden. Deshalb genuegt der reine Objektzugriff, kein zweiter Netzaufruf (D-17).
  //
  // `useSyncExternalStore` statt `useMemo`: der Formularzustand ist uncontrolled, seine
  // Aenderungen laufen ausschliesslich ueber den Zaehlerspeicher. Der Snapshot ist der
  // Formschluessel SELBST, nicht der Zaehler -- React vergleicht ihn mit `Object.is` und
  // rendert nur bei echter Strukturaenderung. Beim Tippen laeuft `kilnShapeKey` zwar erneut
  // (ein Durchlauf ueber wenige Dutzend Eintraege), liefert aber denselben String und loest
  // deshalb KEINEN Render aus -- die Bedingung aus `formStores.ts:3-8`, dass dieser Zaehler
  // nicht zu Parent-State werden darf, bleibt gewahrt.
  const subscribeToValues = useCallback(
    (listener: () => void) => valuesVersionStore?.subscribe(listener) ?? (() => {}),
    [valuesVersionStore],
  );
  const readKilnShapeKey = useCallback(
    () => previousValuesSource.kilnShapeKey(readCurrentKilns()),
    [readCurrentKilns],
  );
  const kilnShapeKeyValue = useSyncExternalStore(
    subscribeToValues,
    readKilnShapeKey,
    readKilnShapeKey,
  );

  const [state, setState] = useState<PreviousValuesLoadState>('off');
  const [previousReport, setPreviousReport] = useState<Record<string, unknown> | null>(null);
  const [totalsFailed, setTotalsFailed] = useState(false);

  // Zwischenspeicher des letzten 'loaded'/'missing'-Laufs. Passt die Kennung wieder, wird der
  // Store daraus befuellt, OHNE erneuten Netzaufruf -- "erst beim Einschalten geladen und
  // danach gecacht". Ein 'error'-Ergebnis wird ausdruecklich NICHT hier abgelegt (D-07): nach
  // einem echten Fehlschlag ist ein Aus/An des Schalters der Weg, es erneut zu versuchen.
  const cache = useRef<CachedRun | null>(null);

  // Ueberholende Antworten: ein Zaehler wird zu Beginn jedes Laufs hochgezaehlt und nach dem
  // await verglichen -- dasselbe Muster wie `validationSeq` in `useReportForm.ts`. Ohne diesen
  // Schutz ueberschriebe eine langsame aeltere Antwort nach einem Werkswechsel den neueren Stand.
  const requestSeq = useRef(0);

  useEffect(() => {
    // Hochgezaehlt wird VOR jeder Verzweigung, nicht erst vor dem Netzaufruf (Befund CR-01 des
    // Code-Reviews). Sonst entwerten die frueh zurueckkehrenden Zweige eine noch laufende
    // Antwort nicht: wer den Schalter waehrend des Abrufs wieder ausschaltet, bekommt vom
    // spaeter eintreffenden `.then` den gerade geleerten Store neu befuellt und den Zustand
    // zurueck auf 'loaded' gesetzt -- Vorwerte sichtbar, Schalter aus. Bei Formular D bliebe
    // ausserdem ein halber Stand stehen (nur die 41 Summen, keiner der 277 Feldwerte), weil
    // der verkettete Rechenaufruf seinen eigenen Weg nimmt. Derselbe Schutz greift beim
    // Cache-Treffer: Werk wechseln und zurueckwechseln darf die erste Antwort nicht mehr
    // durchlassen.
    const seq = ++requestSeq.current;

    if (!enabled) {
      // D-09: ausgeschaltet -> kein Netzaufruf, Store leer.
      store.set({});
      setState('off');
      setPreviousReport(null);
      setTotalsFailed(false);
      return;
    }

    if (!plantId || !previousPeriod) {
      // Die laufende Meldung ist noch nicht geladen -- es gibt noch nichts, wogegen zu
      // vergleichen waere. Bewusst keine Zustandsaenderung.
      return;
    }

    const cacheKey = previousValuesCacheKey(kind, plantId, previousPeriod);
    // Phase 28 (D-15/D-16): Kontext fuer die Ofen-Vorwerte -- `undefined` fuer alle
    // Formulartypen ausser der Umwelterhebung, damit `buildPreviousValues` dort keine
    // `rotaryKilns.*`-Schluessel erzeugt (D-17: kein zweiter Netzaufruf, keine positionalen
    // Werte ohne Kontext).
    // Die Ofenliste wird hier FRISCH gelesen, nicht aus einem Memo uebernommen: der Effekt
    // laeuft, weil sich `kilnShapeKeyValue` geaendert hat, und braucht den Stand von genau
    // diesem Moment.
    const kilnCtx =
      kind === 'environmental'
        ? { currentKilns: readCurrentKilns(), previousYear: previousPeriod.slice(0, 4) }
        : undefined;

    const cached = cache.current;
    if (cached && cached.key === cacheKey) {
      // Im Cache-Zweig werden die Ofenwerte NEU berechnet statt `cached.values` unveraendert zu
      // uebernehmen -- aber nur fuer `kind === 'environmental'` und nur, wenn eine
      // Vorjahresmeldung vorliegt: `kilnShapeKeyValue` in der Abhaengigkeitsliste unten sorgt
      // dafuer, dass dieser Zweig erneut laeuft, wenn sich die aktuelle Ofenliste/Messungsanzahl
      // aendert -- OHNE einen zweiten Netzaufruf (D-17), `hydrate`/`buildPreviousValues` sind
      // beide rein. Fuer alle anderen Formulartypen bleibt der Cache-Zweig unveraendert, sonst
      // gingen die nachgeladenen Formular-D-Summen verloren.
      if (kind === 'environmental' && cached.previousReport) {
        const hydratedCachedReport = hydrate(cached.previousReport as T);
        const recalculated = previousValuesSource.buildPreviousValues(
          kind,
          cached.previousReport,
          hydratedCachedReport,
          kilnCtx,
        );
        store.set(recalculated);
        setState(cached.state);
        setPreviousReport(cached.previousReport);
        setTotalsFailed(cached.totalsFailed);
        cache.current = { ...cached, values: recalculated };
        return;
      }

      store.set(cached.values);
      setState(cached.state);
      setPreviousReport(cached.previousReport);
      setTotalsFailed(cached.totalsFailed);
      return;
    }

    // D-10: 'loading' dient ausschliesslich dazu, dass `previousValuesHintText` in dieser Zeit
    // `null` liefert -- kein Ladeanzeiger, kein gesperrter Schalter, KEIN Anschluss an das
    // seitenweite `loading`-Flag von `fetchReport`. Genau dieses Flag haengte in 20-16 den
    // Formular-Teilbaum aus und kostete den aktiven Reiter.
    setState('loading');

    api
      .getAll({
        plant: plantId,
        reportingPeriod: previousPeriod,
        status: PREVIOUS_VALUE_STATUSES as ReportStatus[],
      })
      .then((list) => {
        if (seq !== requestSeq.current) return; // ueberholte Antwort verwerfen

        const { report, state: resolvedState } = resolvePreviousReport(list);
        const rawReport = report as Record<string, unknown> | null;
        const hydratedReport = report ? hydrate(report as T) : null;
        const values = rawReport && hydratedReport
          ? previousValuesSource.buildPreviousValues(kind, rawReport, hydratedReport, kilnCtx)
          : {};

        // Die 277 Feldwerte stehen, sobald diese Zeile gelaufen ist -- der zweite Aufruf fuer
        // Formular D (unten) haengt sich DANACH an, ohne diesen Schreibvorgang zu verzoegern
        // (D-11: "die Feldwerte warten nicht auf die Summen").
        store.set(values);
        setState(resolvedState);
        setPreviousReport(rawReport);
        setTotalsFailed(false);

        cache.current = {
          key: cacheKey,
          state: resolvedState,
          values,
          previousReport: rawReport,
          totalsFailed: false,
        };

        // D-11: Formular D hat keine persistierten Summenspalten -- seine 41 Summen entstehen
        // erst ueber einen ZWEITEN, unabhaengigen Aufruf desselben zustandslosen
        // Rechenendpunkts, den `useReportCalc` auch fuer die laufende Meldung nutzt. Nicht
        // blockierend (D-10): kein zusaetzlicher Zustand, der einen Ladeanzeiger speisen
        // koennte, kein Anschluss an das seitenweite `loading`-Flag.
        if (kind === 'formular_d' && rawReport && hydratedReport) {
          calculateReport('formular_d', buildFormularDCalcPayload(hydratedReport))
            .then((calcResult) => {
              if (seq !== requestSeq.current) return; // derselbe Ueberholschutz wie beim GET

              const merged = { ...store.get(), ...mapFormularDCalcResult(calcResult) };
              store.set(merged);
              setTotalsFailed(false);

              cache.current = {
                key: cacheKey,
                state: resolvedState,
                values: merged,
                previousReport: rawReport,
                totalsFailed: false,
              };
            })
            .catch((calcErr) => {
              if (seq !== requestSeq.current) return;
              console.error('Failed to fetch previous year Formular D totals:', calcErr);

              // D-21: Scheitert NUR der zweite Aufruf, waehrend der GET gelungen ist, bleiben
              // die 277 Feldwerte stehen -- sie sind geladen und richtig und werden weder
              // verworfen noch durch '–' ersetzt. Die 41 Summenstellen zeigen '–' wie ein
              // leeres Vorjahresfeld (D-08); `mapFormularDCalcResult({})` liefert genau das.
              // `totalsFailed: true` laesst `ReportEditPage` den dritten Satz aus D-21 zeigen
              // ("Die Vorjahres-Summen konnten nicht berechnet werden."), nicht den D-07-Satz,
              // der den 277 sichtbaren Feldwerten widerspraeche. Gecacht wird auch dieser
              // Stand -- D-21 haelt ausdruecklich fest, dass ein Aus/An hier NICHT erneut laedt
              // (Abweichung von D-07, absichtlich, damit die Verifikation sie nicht als
              // Widerspruch meldet).
              const merged = { ...store.get(), ...mapFormularDCalcResult({}) };
              store.set(merged);
              setTotalsFailed(true);

              cache.current = {
                key: cacheKey,
                state: resolvedState,
                values: merged,
                previousReport: rawReport,
                totalsFailed: true,
              };
            });
        }
      })
      .catch((err) => {
        if (seq !== requestSeq.current) return;
        console.error('Failed to fetch previous year report:', err);
        store.set({});
        setState('error');
        setPreviousReport(null);
        setTotalsFailed(false);
        // Kein Cache-Eintrag (D-07): ein Aus/An des Schalters versucht es erneut.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, kind, plantId, reportingPeriod, kilnShapeKeyValue]);

  return { state, previousPeriod, previousReport, totalsFailed };
}
